export const normalizeError = (error: unknown): string => {
  // Handle null/undefined errors
  if (error === null || error === undefined) {
    return 'Unknown error';
  }

  try {
    // RTK Query error shape: { data: { message: ... } }
    if (
      typeof error === 'object' &&
      error !== null &&
      'data' in error &&
      typeof (error as { data?: unknown }).data === 'object' &&
      error.data !== null &&
      'message' in (error as { data: { message?: unknown } }).data &&
      typeof (
        (error as { data: { message?: unknown } }).data as { message?: unknown }
      ).message === 'string'
    ) {
      return (error as { data: { message: string } }).data.message;
    }

    // If error is a plain object with message/statusCode (backend shape)
    if (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof (error as { message?: unknown }).message === 'string'
    ) {
      return (error as { message: string }).message;
    }

    // Define a relaxed shape for common error-like objects
    type ErrorLike = {
      response?: { status?: number; data?: unknown };
      request?: unknown;
      code?: string;
      message?: string;
      error?: string;
    };

    const err = error as ErrorLike;

    // Handle Axios-like response errors
    if (err.response) {
      const status = err.response.status;
      const data = err.response.data as
        | { message?: string; error?: string }
        | undefined;

      // Prefer message, then error, for any status code
      if (data?.message) {
        return data.message;
      }
      if (data?.error) {
        return data.error;
      }

      // Default messages by status code
      switch (status) {
        case 400:
          return 'Bad request';
        case 401:
          return 'Session expired. Please login again';
        case 403:
          return 'You do not have permission to perform this action';
        case 404:
          return 'Resource not found';
        case 429:
          return 'Too many requests. Please try again later';
        case 500:
          return 'Failed to load data. Please try again.';
        case 503:
          return 'Service unavailable. Please try again later';
        default:
          return 'Server error';
      }
    }

    // Handle network errors (request made but no response)
    if (err.request && !err.response) {
      // Check for timeout error
      if (
        err.code === 'ECONNABORTED' ||
        (typeof err.message === 'string' && err.message.includes('timeout'))
      ) {
        return 'Request timeout. Please check your connection';
      }
      return 'Network error. Please check your internet connection';
    }

    // Handle error message string
    if (typeof error === 'string') {
      return error;
    }

    // Handle Error object
    if (
      typeof err === 'object' &&
      err !== null &&
      typeof err.message === 'string'
    ) {
      return err.message;
    }

    // Handle custom error object
    if (
      typeof err === 'object' &&
      err !== null &&
      typeof err.error === 'string'
    ) {
      return err.error;
    }

    return 'Unknown error occurred';
  } catch (e) {
    // Fallback in case of any error during error normalization
    console.error('Error normalizing error:', e);
    return 'Unknown error';
  }
};
